import React, { useEffect } from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid2, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { renderFormElement } from "@helpers/renderFormElement";
import { EmailEditModal } from "@features/settings/SettingsProfile/EmailEditModal";
import { PasswordEditModal } from "@features/settings/SettingsProfile/PasswordEditModal";
import { MandatorConditional } from "@helpers";
import { useUpdateUserProfile } from "@rentablo/api/apiv1";
import { useProfileFormFields } from "./useProfileFormFields";

type Props = {
  user: {
    id: number;
    firstName?: string;
    lastName?: string;
    title?: string;
    gender: string;
    email: string;
    academicTitle?: string;
  };
};

export const ProfileForm = ({ user }: Props) => {
  const { t } = useTranslation();
  const formFields = useProfileFormFields();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [emailEditModalOpen, setEmailEditModalOpen] = React.useState(false);
  const [passwordEditModalOpen, setPasswordEditModalOpen] =
    React.useState(false);

  const { mutate: updateUserProfile } = useUpdateUserProfile({
    onSuccess: () => {
      enqueueSnackbar(t("settings.profile.saveSuccess"), {
        autoHideDuration: 2000,
        variant: "success",
        preventDuplicate: true,
      });
    },
  });

  const schema = Yup.object().shape({
    gender: Yup.string().required(t("form.errors.required")),
    academicTitle: Yup.string().nullable(),
    firstName: Yup.string().required(t("form.errors.required")),
    lastName: Yup.string().required(t("form.errors.required")),
  });

  const { control, watch, formState, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: user,
  });

  const data = watch();

  useEffect(() => {
    reset(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email, reset]);

  const onSubmit = useDebouncedCallback((data: any) => {
    updateUserProfile(
      {
        id: user.id,
        createOrUpdateProfileParams: {
          firstName: data.firstName,
          lastName: data.lastName,
          gender: data.gender === "female" ? 2 : 1,
          academicTitle: data.academicTitle,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["myself"],
          });
        },
      }
    );
  }, 500);

  useEffect(() => {
    if (formState.isValid && !formState.isValidating && formState.isDirty) {
      onSubmit(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.gender,
    data.firstName,
    data.lastName,
    data.academicTitle,
    formState.isValid,
    formState.isValidating,
  ]);

  return (
    <Box>
      <Grid2 container columnSpacing={5} rowSpacing={7} mt={0} mb={7}>
        {formFields.map((field) => {
          return (
            <Grid2
              key={field.name}
              size={{
                xs: 12,
                md: 6,
              }}
            >
              {renderFormElement("settings.profile.form", field, control)}
            </Grid2>
          );
        })}
      </Grid2>
      <MandatorConditional hideFor={["kpmg-germany"]}>
        <>
          <Divider />
          <Box pt={7}>
            <Box mb={5}>
              <Box mb={3}>
                {renderFormElement(
                  "settings.profile.form",
                  {
                    type: "text",
                    name: "email",
                  },
                  control,
                  {
                    disabled: true,
                  }
                )}
              </Box>

              <Link
                onClick={() => setEmailEditModalOpen(true)}
                sx={{
                  cursor: "pointer",
                }}
              >
                {t("settings.profile.emailEditLink")}
              </Link>
            </Box>
            <Divider />
            <Box my={5}>
              <Link
                onClick={() => setPasswordEditModalOpen(true)}
                sx={{
                  cursor: "pointer",
                }}
              >
                {t("settings.profile.passwordEditLink")}
              </Link>
            </Box>
          </Box>
          {emailEditModalOpen && (
            <EmailEditModal
              onClose={() => setEmailEditModalOpen(false)}
              isOpen={emailEditModalOpen}
              email={user.email}
            />
          )}
          {passwordEditModalOpen && (
            <PasswordEditModal
              onClose={() => setPasswordEditModalOpen(false)}
              isOpen={passwordEditModalOpen}
            />
          )}
        </>
      </MandatorConditional>
    </Box>
  );
};
